import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Users, ArrowRight, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { CategoryFilter } from "@/components/site/CategoryFilter";
import { BlogService } from "@/lib/blog-service";
import { cn } from "@/lib/utils";
import { ALL_POSTS } from "@/data/blogPosts";

const POSTS_PER_PAGE = 10;

const BlogComponent = () => {
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [currentPage, setCurrentPage] = useState(1);
  
  const categories = useMemo(() => {
    return ["Todos", ...Object.keys(BlogService.getCategoriesWithCounts(ALL_POSTS))];
  }, []);

  const filteredPosts = useMemo(() => {
    return activeCategory === "Todos" 
      ? ALL_POSTS 
      : ALL_POSTS.filter(post => post.category === activeCategory);
  }, [activeCategory]);

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  
  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [filteredPosts, currentPage]);

  // Reset page when category changes - Using useEffect for side effects
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory]);


  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return (
      <div className="flex flex-wrap justify-center items-center gap-2 py-8">
        <button
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          aria-label="Página anterior"
          className="w-12 h-12 rounded-2xl flex items-center justify-center border border-white/10 bg-white/5 text-muted-foreground hover:border-electric/50 disabled:opacity-30 transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              className="w-12 h-12 rounded-2xl font-black border border-white/10 bg-white/5 text-muted-foreground hover:border-electric/50 transition-all"
            >
              1
            </button>
            {startPage > 2 && <span className="text-muted-foreground px-1">...</span>}
          </>
        )}

        {Array.from({ length: endPage - startPage + 1 }).map((_, i) => {
          const page = startPage + i;
          return (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={cn(
                "w-12 h-12 rounded-2xl font-black transition-all border",
                currentPage === page 
                  ? "bg-electric border-electric text-white shadow-glow scale-110" 
                  : "bg-white/5 border-white/10 text-muted-foreground hover:border-electric/50"
              )}
            >
              {page}
            </button>
          );
        })}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="text-muted-foreground px-1">...</span>}
            <button
              onClick={() => setCurrentPage(totalPages)}
              className="w-12 h-12 rounded-2xl font-black border border-white/10 bg-white/5 text-muted-foreground hover:border-electric/50 transition-all"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          aria-label="Próxima página"
          className="w-12 h-12 rounded-2xl flex items-center justify-center border border-white/10 bg-white/5 text-muted-foreground hover:border-electric/50 disabled:opacity-30 transition-all"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    );
  };

  return (
    <div className="relative">
      <SEOPage
        category="Blog P2PRO painel iptv pos pago"
        title="Blog P2 PRO: Estratégias e Novidades de IPTV"
        subtitle="Estratégias avançadas, tutoriais exclusivos e as últimas tendências do mercado de IPTV e Streaming em 2026."
        variant="centered"
        customHeader={
          <div className="space-y-8">
            <CategoryFilter 
              categories={categories} 
              activeCategory={activeCategory} 
              onCategoryChange={setActiveCategory} 
            />
            {renderPagination()}
          </div>
        }
        blogPosts={paginatedPosts}
        ctaTitle="🚀 Quer Operar com a Melhor Infraestrutura do Brasil?"
        ctaDescription="Não perca mais nenhum cliente por travamento. Junte-se à elite da P2 PRO e tenha o melhor painel pós-pago de 2026."
        ctaButtonText="Falar com Especialista"
        introParagraphs={[
          "Bem-vindo ao centro de inteligência da P2 PRO. Aqui, não apenas fornecemos o melhor sinal de IPTV do Brasil, mas também o conhecimento necessário para você se tornar um líder no mercado digital.",
          "Em 2026, a concorrência amadora está sendo eliminada pela falta de profissionalismo. Nossos artigos são desenhados para elevar o seu nível técnico e comercial, garantindo que sua operação seja lucrativa e escalável."
        ]}
        benefits={[
          {
            title: "Conteúdo Exclusivo",
            description: "Informações estratégicas que você não encontra em nenhum outro lugar do mercado.",
            icon: <Users className="w-6 h-6" />
          },
          {
            title: "Foco em Conversão",
            description: "Tudo o que escrevemos tem um único objetivo: ajudar você a vender mais.",
            icon: <ArrowRight className="w-6 h-6" />
          },
          {
            title: "Atualização Semanal",
            description: "Fique sempre à frente das mudanças tecnológicas e regulatórias do setor.",
            icon: <Clock className="w-6 h-6" />
          }
        ]}
        howItWorks={[
          { title: "Leia", description: "Consuma nossos artigos e guias práticos." },
          { title: "Aplique", description: "Implemente as estratégias em sua revenda." },
          { title: "Escale", description: "Acompanhe o crescimento dos seus lucros." },
          { title: "Domine", description: "Torne-se a referência de IPTV na sua região." }
        ]}
        diferenciais={[
          { title: "Direto da Fonte", description: "Informações técnicas validadas pela nossa equipe de engenharia." },
          { title: "Sem Enrolação", description: "Conteúdo objetivo focado em resultados reais para o seu bolso." }
        ]}
        faq={[
          {
            question: "Os conteúdos são atualizados?",
            answer: "Sim, revisamos nossos guias mensalmente para garantir que as dicas técnicas continuem funcionando perfeitamente."
          }
        ]}
        breadcrumbs={[
          { label: "Elite", href: "/" },
          { label: "Blog", href: "/blog" }
        ]}
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Blog P2 PRO: Estratégias e Novidades IPTV 2026" }, { property: "og:title", content: "Blog P2 PRO: Estratégias e Novidades IPTV 2026" },
      { name: "description", content: "Tendências do mercado de streaming, novidades tecnológicas e estratégias de vendas para revendedores IPTV." }, { property: "og:description", content: "Tendências do mercado de streaming, novidades tecnológicas e estratégias de vendas para revendedores IPTV." },
    ],
  }),
  component: BlogComponent,
});